import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import { PrairiScrollbar, PROFILE_PITCH_THUMB_TRACK_FRACTION } from './PrairiScrollbar';

const PRAIRI_BLACK = '#262626';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

type PitchDeckScrollViewerProps = {
  url: string;
  title?: string;
  className?: string;
  onScrollDepth?: (depth: number) => void;
  /** Copy the profile bar’s thumb size so both pills look the same. */
  matchScrollbarRef?: { readonly current: HTMLElement | null };
  /**
   * Render the scroll pill into this host (outside the grey deck) without
   * changing the deck’s size or background.
   */
  scrollbarHost?: HTMLElement | null;
  /** Exact thumb height in px — match the profile card pill. */
  scrollbarThumbHeight?: number;
  /** Phone dashboard: full-screen upright slides. Swipe up for the next page. */
  phoneSlides?: boolean;
};

const MAX_PAGE_WIDTH = 960;
/** Retina displays need 2× pixels. Cap there so a 3× phone does not quadruple the bitmap. */
const MAX_OUTPUT_PIXEL_RATIO = 2;
/** High enough that slide text stays close to the original PDF. */
const PAGE_JPEG_QUALITY = 0.92;
/** Below the desktop breakpoint the dashboard deck is full-screen phone slides. */
const PHONE_BREAKPOINT = 1024;

function phoneDeckHeight(win: { width: number; height: number }): number {
  return win.height;
}

function useWindowSize(): { width: number; height: number } {
  const [size, setSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1280,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  }));

  useEffect(() => {
    const update = () => setSize({ width: window.innerWidth, height: window.innerHeight });
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  return size;
}

const pdfBytesCache = new Map<string, Promise<Uint8Array>>();
const pageImageCache = new Map<string, string[]>();

function resolvePdfUrl(url: string): string {
  if (!url || url.startsWith('/') || url.startsWith(window.location.origin)) return url;
  return `/__pitchdeck?url=${encodeURIComponent(url)}`;
}

async function fetchPdfBytes(url: string): Promise<Uint8Array> {
  const cached = pdfBytesCache.get(url);
  if (cached) return cached;

  const pending = (async () => {
    const candidates = [resolvePdfUrl(url)];
    if (candidates[0] !== url) candidates.push(url);

    let lastError: unknown;
    for (const src of candidates) {
      try {
        const res = await fetch(src);
        if (!res.ok) throw new Error(`Pitch deck fetch failed (${res.status})`);
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('text/html')) {
          throw new Error('Pitch deck proxy returned HTML');
        }
        return new Uint8Array(await res.arrayBuffer());
      } catch (error) {
        lastError = error;
      }
    }
    pdfBytesCache.delete(url);
    throw lastError;
  })();

  pdfBytesCache.set(url, pending);
  return pending;
}

/** Start the download as soon as the profile opens, before the deck panel paints. */
export function prefetchPitchDeck(url: string): void {
  if (!url || pdfBytesCache.has(url)) return;
  void fetchPdfBytes(url).catch(() => {
    pdfBytesCache.delete(url);
  });
}

function waitForBox(el: HTMLElement | null): Promise<{ width: number; height: number }> {
  return new Promise(resolve => {
    const started = Date.now();
    const tick = () => {
      const width = el?.clientWidth || 0;
      const height = el?.clientHeight || 0;
      if ((width > 0 && height > 0) || Date.now() - started > 2000) {
        resolve({ width: width || 800, height: height || 600 });
        return;
      }
      requestAnimationFrame(tick);
    };
    tick();
  });
}

async function loadPdf(url: string): Promise<pdfjsLib.PDFDocumentProxy> {
  const data = await fetchPdfBytes(url);
  return pdfjsLib.getDocument({ data }).promise;
}

function yieldToPaint(): Promise<void> {
  return new Promise(resolve => {
    setTimeout(resolve, 0);
  });
}

/**
 * Pitch deck scroller with ZERO native overflow scrolling.
 * macOS overlay scrollbars cannot appear because we never use overflow:auto/scroll
 * or element.scrollTop — only transform translateY + the custom #8a8a8a pill.
 */
export const PitchDeckScrollViewer: React.FC<PitchDeckScrollViewerProps> = ({
  url,
  title = 'Pitch Deck',
  className = '',
  onScrollDepth,
  matchScrollbarRef,
  scrollbarHost = null,
  scrollbarThumbHeight,
  phoneSlides = false,
}) => {
  const hoverRootRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef(0);
  const indexRef = useRef(0);
  const touchRef = useRef({
    y: 0,
    startY: 0,
    startOffset: 0,
    active: false,
  });
  const [offset, setOffset] = useState(0);
  const win = useWindowSize();
  const phoneDeck = phoneSlides && win.width < PHONE_BREAKPOINT;
  const [viewportH, setViewportH] = useState(0);
  const pageStepRef = useRef(0);
  const [contentH, setContentH] = useState(0);
  const [pageImages, setPageImages] = useState<string[]>([]);
  const [pageCount, setPageCount] = useState(0);
  const [pagesReady, setPagesReady] = useState(0);
  const [failed, setFailed] = useState(false);
  const [arrowsArmed, setArrowsArmed] = useState(false);
  const lastDepthRef = useRef(0);

  // Measured box, not the window. A window-tall slide inside a shorter deck draws a frame.
  const slideH = phoneDeck ? (viewportH > 0 ? viewportH : phoneDeckHeight(win)) : viewportH;
  const pageStep = slideH;
  pageStepRef.current = pageStep;
  const scrollHeight = phoneDeck
    ? Math.max(pageImages.length * slideH, viewportH)
    : Math.max(
        contentH,
        viewportH,
        pageImages.length > 1 && viewportH > 0 ? viewportH * pageImages.length : 0
      );
  const maxOffset = phoneDeck
    ? Math.max(0, (pageImages.length - 1) * pageStep)
    : Math.max(0, scrollHeight - viewportH);

  const applyOffset = useCallback(
    (next: number) => {
      const clamped = Math.max(0, Math.min(maxOffset, next));
      offsetRef.current = clamped;
      setOffset(clamped);
      return clamped;
    },
    [maxOffset]
  );

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    setPageImages([]);
    setPageCount(0);
    setPagesReady(0);
    setArrowsArmed(false);
    offsetRef.current = 0;
    indexRef.current = 0;
    setOffset(0);

    const load = async () => {
      const pixelRatio = Math.min(window.devicePixelRatio || 1, MAX_OUTPUT_PIXEL_RATIO);
      const cacheKey = `${url}@${pixelRatio}`;
      const cachedImages = pageImageCache.get(cacheKey);
      if (cachedImages && cachedImages.length > 0) {
        setPageImages(cachedImages);
        setPageCount(cachedImages.length);
        setPagesReady(cachedImages.length);
        return;
      }

      try {
        const pdf = await loadPdf(url);
        if (cancelled) return;
        setPageCount(pdf.numPages);

        const { width: containerWidth } = await waitForBox(hoverRootRef.current);
        if (cancelled) return;
        const targetWidth = Math.min(Math.max(containerWidth, 320), MAX_PAGE_WIDTH);

        const images: string[] = [];
        for (let i = 1; i <= pdf.numPages; i += 1) {
          if (cancelled) return;
          const page = await pdf.getPage(i);
          const unscaled = page.getViewport({ scale: 1 });
          const cssScale = Math.max(targetWidth / unscaled.width, 0.5);
          const viewport = page.getViewport({ scale: cssScale * pixelRatio });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          if (!context) continue;
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          context.imageSmoothingEnabled = true;
          context.imageSmoothingQuality = 'high';
          context.fillStyle = '#ffffff';
          context.fillRect(0, 0, canvas.width, canvas.height);
          await page.render({ canvasContext: context, viewport }).promise;
          images.push(canvas.toDataURL('image/jpeg', PAGE_JPEG_QUALITY));
          pageImageCache.set(cacheKey, [...images]);
          if (!cancelled) {
            setPageImages([...images]);
            setPagesReady(images.length);
          }
          await yieldToPaint();
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [url]);

  // Measure viewport + content for transform scrolling (no native overflow).
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return;

    const measure = () => {
      setViewportH(viewport.clientHeight);
      setContentH(content.scrollHeight);
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(viewport);
    ro.observe(content);
    return () => ro.disconnect();
  }, [pageImages.length, pagesReady, phoneDeck]);

  useEffect(() => {
    if (!phoneDeck || pageStep <= 0 || touchRef.current.active) return;
    applyOffset(indexRef.current * pageStep);
  }, [phoneDeck, pageStep, applyOffset]);

  useEffect(() => {
    if (!onScrollDepth || maxOffset <= 0) return;
    const depth = Math.round((offset / maxOffset) * 100);
    if (Math.abs(depth - lastDepthRef.current) < 10) return;
    lastDepthRef.current = depth;
    onScrollDepth(depth);
  }, [offset, maxOffset, onScrollDepth]);

  const gestureRef = useRef({
    phoneDeck: false,
    pageCount: 0,
    maxOffset: 0,
    applyOffset,
  });
  gestureRef.current.phoneDeck = phoneDeck;
  gestureRef.current.pageCount = pageImages.length;
  gestureRef.current.maxOffset = maxOffset;
  gestureRef.current.applyOffset = applyOffset;

  // Wheel + touch → transform offset (never native scroll).
  const deckReady = pageImages.length > 0;
  useEffect(() => {
    const root = hoverRootRef.current;
    if (!root || !deckReady) return;

    const wheelLock = { until: 0 };
    const onWheel = (event: WheelEvent) => {
      // Always stop native/page scroll over the deck — never let macOS draw a bar here.
      event.preventDefault();
      event.stopPropagation();
      const gesture = gestureRef.current;
      if (gesture.maxOffset <= 0) return;
      if (gesture.phoneDeck) {
        const now = performance.now();
        if (now < wheelLock.until) return;
        const page = pageStepRef.current;
        if (page <= 0 || Math.abs(event.deltaY) < 8) return;
        wheelLock.until = now + 450;
        const next = Math.max(
          0,
          Math.min(gesture.pageCount - 1, indexRef.current + (event.deltaY > 0 ? 1 : -1))
        );
        indexRef.current = next;
        gesture.applyOffset(next * page);
        return;
      }
      gesture.applyOffset(offsetRef.current + event.deltaY);
    };

    const snapPhone = (direction: number) => {
      const gesture = gestureRef.current;
      const page = pageStepRef.current;
      touchRef.current.active = false;
      if (page <= 0) return;
      const next = Math.max(0, Math.min(gesture.pageCount - 1, indexRef.current + direction));
      indexRef.current = next;
      gesture.applyOffset(next * page);
    };
    const onTouchStart = (event: TouchEvent) => {
      const gesture = gestureRef.current;
      const y = event.touches[0]?.clientY ?? 0;
      // Stop the page from taking the swipe, which was cancelling the slide change.
      if (gesture.phoneDeck) event.preventDefault();
      touchRef.current.y = y;
      touchRef.current.startY = y;
      touchRef.current.startOffset = offsetRef.current;
      touchRef.current.active = gesture.phoneDeck;
    };
    const onTouchMove = (event: TouchEvent) => {
      const gesture = gestureRef.current;
      const y = event.touches[0]?.clientY ?? touchRef.current.y;
      if (gesture.phoneDeck) {
        event.preventDefault();
        // Finger up moves to the next slide. The page stays upright, so the finger and the slide match.
        const delta = touchRef.current.startY - y;
        touchRef.current.y = y;
        gesture.applyOffset(touchRef.current.startOffset + delta);
        return;
      }
      const dy = touchRef.current.y - y;
      touchRef.current.y = y;
      if (Math.abs(dy) < 0.5) return;
      // Keep the finger on the slides. At the first or last slide, the same swipe moves the page.
      event.preventDefault();
      const atStart = offsetRef.current <= 0 && dy < 0;
      const atEnd =
        gesture.maxOffset <= 0 || (offsetRef.current >= gesture.maxOffset - 1 && dy > 0);
      if (atStart || atEnd) {
        window.scrollBy(0, dy);
        return;
      }
      gesture.applyOffset(offsetRef.current + dy);
    };
    const finishPhoneSlide = (endY: number) => {
      if (!gestureRef.current.phoneDeck) return;
      const delta = touchRef.current.startY - endY;
      if (delta > 36) snapPhone(1);
      else if (delta < -36) snapPhone(-1);
      else snapPhone(0);
    };
    const onTouchEnd = (event: TouchEvent) => {
      const point = event.changedTouches[0];
      finishPhoneSlide(point?.clientY ?? touchRef.current.startY);
    };
    const onTouchCancel = () => {
      if (!gestureRef.current.phoneDeck) return;
      snapPhone(0);
    };

    root.addEventListener('wheel', onWheel, { passive: false });
    root.addEventListener('touchstart', onTouchStart, { passive: false });
    root.addEventListener('touchmove', onTouchMove, { passive: false });
    root.addEventListener('touchend', onTouchEnd);
    root.addEventListener('touchcancel', onTouchCancel);
    return () => {
      root.removeEventListener('wheel', onWheel);
      root.removeEventListener('touchstart', onTouchStart);
      root.removeEventListener('touchmove', onTouchMove);
      root.removeEventListener('touchend', onTouchEnd);
      root.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [deckReady]);

  // Click once, then arrow keys move one slide via transform (no scrollTop).
  useEffect(() => {
    const root = hoverRootRef.current;
    const content = contentRef.current;
    if (!root || !content || pageImages.length === 0) return;

    const pageOffsets = (): number[] => {
      const pages = Array.from(content.querySelectorAll<HTMLElement>('[data-pitch-page]'));
      const horizontal = pages.length > 1 && pages[1].offsetLeft > pages[0].offsetLeft + 4;
      return pages.map(el => (horizontal ? el.offsetLeft : el.offsetTop));
    };

    const goToPage = (delta: number) => {
      const offsets = pageOffsets();
      if (offsets.length === 0) return;
      const y = offsetRef.current;
      let index = 0;
      for (let i = 0; i < offsets.length; i += 1) {
        if (offsets[i] <= y + 4) index = i;
        else break;
      }
      const next = Math.max(0, Math.min(offsets.length - 1, index + delta));
      applyOffset(offsets[next]);
    };

    const onPointerDownRoot = () => {
      setArrowsArmed(true);
      root.focus({ preventScroll: true });
    };

    const onPointerDownDoc = (event: PointerEvent) => {
      if (!(event.target instanceof Node)) return;
      if (!root.contains(event.target)) setArrowsArmed(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (!arrowsArmed) return;
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (
        event.target instanceof HTMLElement &&
        (event.target.tagName === 'INPUT' ||
          event.target.tagName === 'TEXTAREA' ||
          event.target.isContentEditable)
      ) {
        return;
      }

      if (
        event.key === 'ArrowRight' ||
        event.key === 'ArrowDown' ||
        event.key === 'PageDown' ||
        event.key === ' '
      ) {
        event.preventDefault();
        event.stopPropagation();
        goToPage(1);
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp' || event.key === 'PageUp') {
        event.preventDefault();
        event.stopPropagation();
        goToPage(-1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        applyOffset(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        const offsets = pageOffsets();
        applyOffset(offsets[offsets.length - 1] ?? maxOffset);
      }
    };

    root.addEventListener('pointerdown', onPointerDownRoot);
    document.addEventListener('pointerdown', onPointerDownDoc, true);
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      root.removeEventListener('pointerdown', onPointerDownRoot);
      document.removeEventListener('pointerdown', onPointerDownDoc, true);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [pageImages.length, arrowsArmed, applyOffset, maxOffset]);

  if (failed) {
    return (
      <div
        className={`relative h-full min-h-0 ${className}`}
        style={{ backgroundColor: PRAIRI_BLACK }}
      >
        <iframe
          title={title}
          src={`${url}#toolbar=0&navpanes=0&view=FitH`}
          className="h-full w-full border-0"
        />
      </div>
    );
  }

  const metrics =
    viewportH > 0 ? { scrollTop: offset, scrollHeight, clientHeight: viewportH } : null;

  const scrollBar = phoneDeck ? null : (
    <PrairiScrollbar
      metrics={metrics}
      onSeek={applyOffset}
      matchRef={matchScrollbarRef}
      hoverRootRef={hoverRootRef}
      forceShow
      inHost={Boolean(scrollbarHost)}
      thumbHeight={scrollbarThumbHeight}
      constantTrackFraction={
        scrollbarThumbHeight && scrollbarThumbHeight > 0
          ? undefined
          : PROFILE_PITCH_THUMB_TRACK_FRACTION
      }
      contentKey={`${pageCount}-${pageImages.length}-${pagesReady}-${viewportH}-${scrollHeight}`}
    />
  );

  return (
    <>
      <div
        ref={hoverRootRef}
        tabIndex={0}
        role="region"
        aria-label={title}
        className={`group/deck relative h-full min-h-0 overflow-hidden outline-none focus:outline-none touch-none ${className}`}
        style={{
          backgroundColor: phoneDeck ? '#ffffff' : PRAIRI_BLACK,
          overscrollBehavior: 'none',
          height: phoneDeck ? '100%' : undefined,
        }}
      >
        {pagesReady === 0 && (
          <div
            className={`absolute inset-0 z-10 flex items-center justify-center ${
              phoneDeck ? 'bg-white' : 'bg-[#262626]'
            }`}
          >
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-500 border-t-white" />
          </div>
        )}
        <div ref={viewportRef} className="absolute inset-0 overflow-hidden">
          <div
            ref={contentRef}
            className="flex w-full flex-col will-change-transform"
            style={{ transform: `translate3d(0, ${-offset}px, 0)` }}
          >
            {pageImages.map((src, index) =>
              phoneDeck ? (
                <div
                  key={`${url}-${index}`}
                  data-pitch-page={index}
                  className="relative w-full shrink-0 overflow-hidden bg-white"
                  style={{ height: slideH > 0 ? slideH : '100%' }}
                >
                  <img
                    src={src}
                    alt={`${title} page ${index + 1}`}
                    draggable={false}
                    className="absolute max-w-none object-cover"
                    style={{
                      left: -4,
                      top: -4,
                      width: 'calc(100% + 8px)',
                      height: 'calc(100% + 8px)',
                    }}
                  />
                </div>
              ) : (
                <img
                  key={`${url}-${index}`}
                  data-pitch-page={index}
                  src={src}
                  alt={`${title} page ${index + 1}`}
                  className="block w-full"
                  draggable={false}
                />
              )
            )}
          </div>
        </div>
        {!scrollbarHost ? scrollBar : null}
      </div>
      {scrollbarHost ? createPortal(scrollBar, scrollbarHost) : null}
    </>
  );
};
